import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { formatMoney } from "@/lib/format";
import { Modal, FieldText } from "./Customers";

const EMPTY = { name: "", description: "", price: 0, currency: "SEK", vat_rate: 25, category: "flight", image_url: "", active: true };

export default function Services() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try { const { data } = await api.get("/services"); setList(data); }
    catch { toast.error("Failed to load services"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price) || 0, vat_rate: Number(form.vat_rate) || 0 };
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Saved");
      setShowForm(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (s) => {
    if (!confirm(`Delete "${s.name}"?`)) return;
    try { await api.delete(`/services/${s.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <AdminLayout title="Services">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[color:var(--ivory-muted)]">{list.length} service{list.length !== 1 ? "s" : ""}</div>
        <button onClick={openCreate} className="pill pill-primary inline-flex items-center gap-2 text-sm" data-testid="add-service-button">
          <Plus className="w-4 h-4" /> Add service
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm" data-testid="services-table">
          <thead className="bg-white/[0.03] text-[color:var(--ivory-muted)] text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Price</th>
              <th className="px-4 py-3 text-left">VAT %</th>
              <th className="px-4 py-3 text-left">Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`service-row-${s.id}`}>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 capitalize">{s.category || "—"}</td>
                <td className="px-4 py-3">{Number(s.price) > 0 ? formatMoney(s.price, s.currency) : "—"}</td>
                <td className="px-4 py-3">{s.vat_rate}%</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${s.active ? "bg-[color:var(--bd-green)]/20 text-[color:var(--bd-green-bright)]" : "bg-white/[0.06] text-[color:var(--ivory-muted)]"}`}>
                    {s.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(s)} className="p-2 hover:text-white text-[color:var(--ivory-muted)]" data-testid={`edit-service-${s.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(s)} className="p-2 hover:text-[color:var(--bd-red-bright)] text-[color:var(--ivory-muted)]" data-testid={`delete-service-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan="6" className="p-12 text-center text-[color:var(--ivory-muted)]">No services yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title={editing ? "Edit service" : "Add service"}>
          <form onSubmit={save} className="space-y-4" data-testid="service-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldText label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Select label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={[
                { value: "flight", label: "Flight" }, { value: "visa", label: "Visa" }, { value: "other", label: "Other" },
              ]} />
              <FieldText label="Price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
              <Select label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} options={[
                { value: "SEK", label: "SEK" }, { value: "BDT", label: "BDT" },
              ]} />
              <FieldText label="VAT rate (%)" type="number" value={form.vat_rate} onChange={(v) => setForm({ ...form, vat_rate: v })} />
              <FieldText label="Image URL (optional)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="email-input pl-4 py-3" style={{ borderRadius: 12, resize: "vertical" }} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span>Visible on public website</span>
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="pill border border-white/15 text-white">Cancel</button>
              <button type="submit" className="pill pill-primary" data-testid="service-save">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
}

export function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="email-input pl-4 appearance-none cursor-pointer" style={{ paddingRight: 32 }}>
        {options.map((o) => <option key={o.value} value={o.value} style={{ background: "#0E1216" }}>{o.label}</option>)}
      </select>
    </div>
  );
}
